import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React from 'react'
import { SettingsBox } from 'src/Elements/Data'
import { SingleToggleSettingBox } from 'src/Elements/SystemSettings/forms'
import { DppBatteryStatus, DynamicPricesSettings } from '../../types'
import { FORM_DEPTH } from './battery-form.constants'
import DppBatteryStatusDropdown from './DppBatteryStatusDropdown'
import { Box, Button, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useIsDynamicEmsAccessible } from '../../hooks'
import { useFormikContext } from 'formik'

type Props = {
  hasWritingRights: boolean
  dppBatteryStatus: DppBatteryStatus
  setDppBatteryStatus: (value: DppBatteryStatus) => void
}

/**
 * If EMS is available for a given installation, this component will render a dropdown component with three options,
 * otherwise it renders a simple toggle component.
 */
const DppBatteryToggle: React.FC<Props> = ({ hasWritingRights, dppBatteryStatus, setDppBatteryStatus }) => {
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { isDynamicEmsAccessible, isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const { dirty } = useFormikContext<DynamicPricesSettings>()

  const handleAi360SettingsClick = (): void => {
    navigate(`/dashboard/ai360/${installationId}/${storageId}`)
  }

  if (isDynamicEmsAccessible) {
    return (
      <SettingsBox
        title={t('systemSettings-dynamic-prices.battery.ems-based-toggle-battery-dynamic-power-prices')}
        suffix={
          <Box flex={1} display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems="center" justifyContent="end" gap={2}>
            <DppBatteryStatusDropdown dppBatteryStatus={dppBatteryStatus} setDppBatteryStatus={setDppBatteryStatus} disabled={!hasWritingRights} />
            {isDynamicEmsV2Accessible && dppBatteryStatus === 'enabledWithDynamicEms' && !dirty && (
              <Button variant="text" sx={{ mt: { xs: 3, sm: 0 }, textTransform: 'uppercase' }} onClick={handleAi360SettingsClick}>
                {t('systemSettings-dynamic-prices.battery.ai360-settings')}
              </Button>
            )}
          </Box>
        }
        depth={FORM_DEPTH}
        disabled={!hasWritingRights}
      />
    )
  }

  return (
    <SingleToggleSettingBox
      title={t('systemSettings-dynamic-prices.battery.toggle-battery-dynamic-power-prices')}
      name="dynamicPricesForBatteryEnabled"
      disabled={!hasWritingRights}
      description={t('systemSettings-dynamic-prices.battery.description')}
      depth={FORM_DEPTH}
    />
  )
}

export default DppBatteryToggle
